export const translations = {
  en: {
    nav: {
      cta: "Book Growth Review"
    },
    hub: {
      liveProof: "Learn what the AI Lead Assistant can do →",
      contentDay: "Explore the Content Day →",
      aiAutomation: "See the AI Lead Assistant →"
    },
    hero: {
      h1: "You don't need more content.",
      h1Span: "You need a system that consistently brings in customers.",
      h2: "Great businesses fail every day because nobody knows they exist. I fix that. I build customer acquisition systems that turn strangers into paying clients.",
      p: "I help local businesses turn their expertise into clear messaging, premium content and simple sales systems that generate more leads.",
      cta: "Book a free Growth review"
    },
    liveDemo: {
      title: "Experience It Live: DM Automation",
      body: "Don't just take my word for it. Test our DM acquisition system in real-time. Click below, send me a DM with the word 'SYSTEM' on Instagram, and watch the bot qualify you instantly.",
      cta: "Test it on Instagram"
    },
    beautyClinic: {
      hero: {
        h1: "Turn Instagram Enquiries Into More Beauty Appointments",
        h2: "Your clinic is already getting DMs. The problem is what happens after someone asks: 'How much is Botox?', 'Do you offer this treatment?', 'Can I book this week?'",
        body: "Your AI Lead Assistant responds instantly, answers common questions, qualifies enquiries and guides the right prospects toward a consultation or appointment.",
        pricing: "€99/mo + 10% of attributed first-treatment revenue | No new salesperson. No complicated CRM. No ad spend required.",
        cta: "Book a Free Growth Review →"
      },
      problem: {
        h2: "Your Instagram DMs are full of opportunities.",
        subtext: "But every unanswered question, slow reply or forgotten follow-up can mean a lost appointment.",
        items: [
          {
            title: "Slow responses",
            desc: "Potential clients move on when they don't get an answer within minutes."
          },
          {
            title: "Repetitive questions",
            desc: "Your team keeps answering the same questions about treatments and pricing."
          },
          {
            title: "Unqualified enquiries",
            desc: "Your team spends time on conversations that never become actual appointments."
          },
          {
            title: "Lost follow-ups",
            desc: "Conversations disappear because there is no system to keep them moving forward."
          }
        ],
        closing: "You don't necessarily need more enquiries. You need to handle the ones you already have better."
      },
      howItWorks: {
        h2: "Your AI Lead Assistant works inside the conversation.",
        items: [
          {
            title: "Respond instantly",
            desc: "New enquiries get an immediate response without waiting for your busy team."
          },
          {
            title: "Understand the request",
            desc: "The agent reads the context and adapts to what the person actually wants."
          },
          {
            title: "Answer common questions",
            desc: "Treatment info, availability, and pricing — based on information you provide."
          },
          {
            title: "Qualify",
            desc: "Identify whether the person is a serious prospect and filter out the noise."
          },
          {
            title: "Move toward booking",
            desc: "Guide qualified prospects toward a consultation or appointment link."
          },
          {
            title: "Follow up",
            desc: "Re-engage conversations that go cold when they stop replying."
          }
        ]
      },
      journey: {
        h2: "From Instagram DM to Appointment",
        steps: ["Instagram DM", "Instant Response", "Q&A", "Qualification", "Consultation", "Appointment"],
        subtext: "Your team spends less time answering repetitive questions and more time with actual clients."
      },
      trust: {
        h2: "Built for real clinic conversations",
        medical: "IMPORTANT: The AI Lead Assistant does not make diagnoses, give individual medical advice, or recommend unapproved treatments. It only uses information explicitly approved by your clinic.",
        cards: [
          {
            title: "Treatment Questions",
            desc: "'Is this laser safe for dark skin?'"
          },
          {
            title: "Price Questions",
            desc: "'How much is a single session vs a pack?'"
          },
          {
            title: "Availability",
            desc: "'Do you have any slots open this Friday?'"
          },
          {
            title: "Qualification",
            desc: "'Have you had this treatment before?'"
          },
          {
            title: "Booking",
            desc: "'Can I book my consultation now?'"
          },
          {
            title: "Follow-up",
            desc: "'Still interested in that session?'"
          }
        ]
      },
      positioning: {
        h2: "This isn't a chatbot.",
        text: "Most chatbots are built to answer questions. This system is built to move the conversation forward.",
        logic: ["Question", "Context", "Qualification", "Next step"]
      },
      whyClinics: {
        h2: "Why this works for aesthetic businesses",
        items: ["Repetitive questions about treatment details.", "Time-sensitive enquiries (speed-to-lead).", "Easy to qualify based on simple criteria.", "Directly connected to a high-value booking."],
        conclusion: "Your team shouldn't handle every first interaction. The AI handles the first layer. Your team handles the client."
      },
      fit: {
        do: {
          title: "When this works best",
          items: ["Regular Instagram enquiries", "Clear treatment offer", "Defined booking process", "FAQs and approved info"]
        },
        dont: {
          title: "Not a good fit if",
          items: ["You rarely receive enquiries", "Your offer is still unclear", "You expect AI to create demand from zero"]
        }
      },
      pricing: {
        h2: "AI Lead Assistant",
        price: "€99 / month + 10% first-treatment revenue",
        included: ["Instagram DM AI assistant", "Custom conversation logic", "Lead qualification", "FAQ handling", "Booking guidance", "Follow-up automation", "Ongoing optimisation"]
      },
      implementation: {
        steps: [
          {
            title: "1. Map Flow",
            desc: "We map what people ask and when to book."
          },
          {
            title: "2. Build Agent",
            desc: "Configured around your approved clinic info."
          },
          {
            title: "3. Test",
            desc: "Simulate real scenarios before going live."
          },
          {
            title: "4. Go Live",
            desc: "System starts handling the first layer."
          },
          {
            title: "5. Optimise",
            desc: "Improve responses based on real data."
          }
        ]
      },
      proof: {
        h2: "Don't take my word for it. Talk to the AI yourself.",
        body: "I'm using the same system on my own Instagram that I offer to local businesses. Send 'SYSTEM' on Instagram and experience it yourself.",
        cta: "Test the AI →"
      },
      finalCta: {
        h2: "Could your clinic benefit from this?",
        body: "Book a free 15-minute Growth Review. We'll look at how your Instagram enquiries are currently handled and see whether an AI Lead Assistant could save your team time.",
        cta: "Book My Growth Review →"
      }
    },
    aiLeadAgent: {
      hero: {
        h1: "Meet Your AI Lead Assistant",
        h2: "Your Instagram DMs shouldn't just be conversations. They should create customers.",
        subH2: "Built to turn Instagram conversations into qualified opportunities.",
        body: "The assistant responds to enquiries, answers questions, qualifies prospects and guides the right people toward a booking — automatically.",
        cta: "See It In Action →",
        proof: "You're already talking to it. Test it yourself. → Send 'SYSTEM' on Instagram."
      },
      whatItDoes: {
        h2: "What happens when someone messages your business?",
        items: [
          {
            title: "Respond instantly",
            desc: "The faster you respond, the less likely a promising enquiry is to go cold."
          },
          {
            title: "Understand the prospect",
            desc: "The agent reads the conversation and adapts its questions to the specific needs of the person."
          },
          {
            title: "Answer common questions",
            desc: "Answers common questions about your services, availability, pricing and next steps — based on the information you provide."
          },
          {
            title: "Qualify",
            desc: "Identify serious prospects and prioritize the conversations that deserve your team's attention."
          },
          {
            title: "Move them forward",
            desc: "Guide qualified leads toward a booking and reduce the back-and-forth around scheduling."
          },
          {
            title: "Follow up",
            desc: "Automatically re-engages conversations that go cold, ensuring no lead is left behind."
          }
        ]
      },
      flow: {
        h2: "The Seamless Journey",
        steps: ["Instagram DM", "Response", "Conversation", "Qualification", "Booking"],
        teamHandover: "Your team takes it from there.",
        subtext: "No forms. No complicated funnels. Just a better conversation."
      },
      capabilities: {
        h2: "The Capabilities",
        items: [
          {
            title: "Human-like conversations",
            desc: "The AI understands context and adapts its responses instead of following a rigid, robotic script."
          },
          {
            title: "Understands more than text",
            desc: "Prospects can send voice notes or images, and the agent uses that info to provide better answers."
          },
          {
            title: "Appointment booking",
            desc: "Guide qualified leads toward a booking and reduce the back-and-forth around scheduling."
          },
          {
            title: "Automated follow-up",
            desc: "If a prospect stops replying, the agent gently follows up to bring them back to the booking step."
          },
          {
            title: "Instagram Native",
            desc: "Operates directly where your audience already spends their time, removing platform friction."
          }
        ]
      },
      difference: {
        text: "This isn't a chatbot. Most chatbots just answer questions. This system is built around moving a conversation forward.",
        logic: ["Question", "Context", "Qualification", "Next step"]
      },
      whoIsItFor: {
        h2: "Built for businesses that already get enquiries",
        disclaimer: "CRITICAL: If you don't get meaningful inbound enquiries yet, an AI DM agent probably isn't your first priority.",
        items: [
          {
            title: "Gyms",
            desc: "Qualify enquiries, answer common questions and help turn Instagram conversations into booked trials."
          },
          {
            title: "Coaches",
            desc: "Filter prospects + book discovery calls on autopilot."
          },
          {
            title: "Local services",
            desc: "Turn initial enquiries into firm quotes or bookings."
          },
          {
            title: "Clinics",
            desc: "Handle FAQs + qualify patients + book appointments."
          },
          {
            title: "Hospitality",
            desc: "Manage availability, bookings, and common guest questions."
          }
        ]
      },
      comparison: {
        sense: {
          title: "When an AI Lead Assistant makes sense",
          items: ["You already receive enquiries.", "Your team spends time answering repetitive questions.", "Some prospects disappear before booking.", "You want to respond faster without hiring another salesperson."]
        },
        noSense: {
          title: "When it doesn't",
          items: ["You rarely get inbound enquiries.", "Your offer isn't clear yet.", "You're still figuring out who your ideal customer is."]
        }
      },
      proof: {
        h2: "Don't read about it. Talk to it.",
        subH2: "You're looking at a page about an AI sales assistant. So instead of explaining how it works, test it yourself.",
        steps: ["Open Instagram", "Send 'SYSTEM'", "Have a real conversation", "See whether it can qualify you"],
        cta: "Talk to the AI →"
      },
      finalCta: {
        h2: "Could this work for your business?",
        body: "Book a free 15-minute Growth Review and we'll look at your current enquiry flow and identify where automation could save time, improve follow-up or help more prospects reach the next step.",
        cta: "Book a Growth Review →"
      }
    },
    guarantee: {
      title: "The Guarantee",
      body: "If you don't generate 5 qualified leads in 14 days, I keep working at no additional cost until we do. Guarantee applies when the agreed offer, lead database, booking system and response process are implemented and actively used."
    },
    fitness: {
      seo: {
        title: "Unlock €500–€1,500 In 14 Days | Mathias Deppe",
        description: "ATTENTION BARCELONA GYM OWNERS: Unlock €500–€1,500 In 14 Days From Your 'Dead' Lead List — With €0 Ad Spend. Risk-Free Guarantee."
      },
      hero: {
        tag: "ATTENTION BARCELONA GYM OWNERS",
        h1: "Unlock €500–€1,500 In 14 Days From Your 'Dead' Lead List",
        h1Sub: "— With €0 Ad Spend.",
        hook: "You don't need more software logins, generic agency retainers, or hours spent dancing on TikTok.",
        p: "You don't need another complex software login or a generic agency retainer. You need liquid cash in your bank account. I show up at your gym in Barcelona, shoot cinema-grade content, and turn your old, unclosed leads into paying high-ticket members within 48 hours.",
        pricing: "€500 Setup + €249/mo + €30 per new member | No new salesperson. No ad spend required.",
        cta: "Book a Free Growth Review →"
      },
      vip: {
        header: "The 'Barcelona Local' VIP Kickoff Package (€4,500 Real Value)",
        intro: "I don't just send you 'how-to' videos. I show up at your facility with pro gear to build your business assets for you.",
        items: [
          {
            title: "3-Hour On-Site Pro Content Shoot (€1,500 Value)",
            desc: "I bring cinema-grade gear to your gym in Barcelona. We capture 20+ pro photos and 8 high-converting reels to make you the #1 authority in the city."
          },
          {
            title: "Complete System & Plumbing Setup (€1,000 Value)",
            desc: "Custom high-ticket landing pages, automated booking calendars, and 5-minute SMS response infrastructure."
          },
          {
            title: "The 14-Day Cash Injection Campaign (€1,000 Value)",
            desc: "A dedicated database reactivation sequence for your old leads. We turn 'dead' contacts into cash with €0 in ad spend."
          },
          {
            title: "24/7 Autopilot DM Booking Engine (€1,000 Real Value)",
            desc: "We fully build, script, and launch automated Instagram/WhatsApp conversation flows inside your accounts. You never miss an inbound lead or waste hours answering basic questions in DMs again."
          }
        ]
      },
      comparison: {
        h2: "The Agency & Consultant Trap",
        p: "Most agencies give you a CRM and tell you to film yourself. Most consultants give you advice but no execution. I am the bridge.",
        headers: ["The Agency Trap", "The Consultant Trap", "The MD System"],
        agency: {
          title: "The Agency Trap",
          points: ["Hand you a complex CRM", "Demand you film on your phone", "Force €2k+ ad spend monthly", "Lock you into 6-month retainers"]
        },
        consultant: {
          title: "The Consultant Trap",
          points: ["Give you 'strategy' PDFs", "Leave the work to you", "No technical implementation", "Charged by the hour"]
        },
        md: {
          title: "The MD System",
          points: ["I build all pages & automations", "I show up with pro gear to shoot", "€0 ad spend for Phase 1", "Strict 14-Day Result Guarantee"]
        }
      },
      howItWorks: {
        h2: "The 4-Phase Growth System",
        phases: [
          {
            title: "Phase 1: The Cash Injection",
            desc: "We send a 9-word text blast to your dead lead list. We turn old contacts into immediate cash within 48 hours."
          },
          {
            title: "Phase 2: The Grand Slam Offer",
            desc: "We replace your €120/mo commodity membership with a €500 6-Week Transformation Challenge. High-ticket cash upfront."
          },
          {
            title: "Phase 3: The 30-Second 'Speed-to-Lead' Inbound Engine",
            desc: "We install our pre-scripted Instagram, Facebook, and WhatsApp DM automation into your accounts. When a local lead comments on a Reel, clicks an ad, or messages your page, our system instantly engages them in 30 seconds, pre-qualifies them, and drops your 6-Week Challenge booking link directly in the chat while you sleep."
          },
          {
            title: "Phase 4: The 12-Month Rollover",
            desc: "We convert challenge participants into long-term contracts. You keep the upfront cash and secure recurring revenue."
          }
        ]
      },
      investment: {
        h2: "Performance-Based Pricing",
        intro: "I don't hide behind 'brand awareness.' I price based on the systems I build and the results they generate.",
        setup: {
          title: "The Build",
          price: "€2,000 Setup Fee",
          desc: "Full funnel build, copy, automations, and the 14-day cash injection campaign."
        },
        tier1: {
          title: "The Minimum",
          price: "€500/mo",
          desc: "Ad management & automation maintenance. You shoot raw phone clips; I handle the technical side."
        },
        tier2: {
          title: "The Premium",
          price: "€1,500/mo",
          desc: "Full management PLUS a 3-hour pro content shoot at your gym in Barcelona every single month."
        },
        waived: {
          title: "The Partner Offer",
          price: "€0",
          desc: "Commit to 12 months on either tier, and I waive the €2,000 setup fee entirely."
        }
      },
      faq: {
        h2: "Hard Truths (FAQ)",
        items: [
          {
            q: "Why don't you just give me the software and let me do it?",
            a: "Because software doesn't build businesses—systems do. You're a gym owner, not a systems engineer. I build the plumbing so you can focus on training your members."
          },
          {
            q: "I've been burned by agencies before. Why is this different?",
            a: "Agencies focus on 'leads.' I focus on 'cash.' We start with your existing list (Phase 1) to get you a ROI before you even spend money on ads. If I don't perform, I work for free."
          },
          {
            q: "Do I have to be on camera?",
            a: "Yes, but I make it easy. I come to your gym, set up the lights, and direct you. You don't need to be an actor; you just need to be the expert you already are."
          }
        ]
      },
      guarantee: {
        title: "The Barcelona Lead Guarantee",
        desc: "If you don't generate 5 qualified leads in 14 days, I keep working at no additional cost until we do. Guarantee applies when the agreed offer, lead database, booking system and response process are implemented and actively used.",
        cta: "Claim Your 14-Day Cash Injection"
      }
    },
    problem: {
      h2: "You know your business is great.",
      h2Span: "So why aren't more people buying?",
      points: [
        {
          title: "Unclear Messaging",
          desc: "Your audience doesn't understand exactly what you do or why it matters."
        },
        {
          title: "Inconsistent Content",
          desc: "You post when you have time, which means you never build momentum."
        },
        {
          title: "No Customer Journey",
          desc: "There is no clear path from seeing a post to becoming a paying client."
        },
        {
          title: "No Follow-up",
          desc: "Leads slip through the cracks because you don't have a system to nurture them."
        }
      ],
      result: "The result? People like your business…",
      resultSpan: "but never become customers.",
      cta: "See how we solved this for others →",
      viewAll: "View all Case Studies →"
    },
    caseStudies: {
      header: "Case Studies / Results",
      viewFull: "Read Full Case Study",
      back: "← Back to Case Studies",
      challenge: "The Challenge",
      solution: "The Solution",
      results: "The Results"
    },
    deliverables: {
      h2: "Here's what working together looks like",
      items: [
        {
          title: "1 Monthly Content Day",
          desc: "A full day dedicated to capturing all the assets you need."
        },
        {
          title: "20+ Professional Photos",
          desc: "High-end photography that positions you as an authority."
        },
        {
          title: "6–8 Short-form Videos",
          desc: "Viral-ready reels and TikToks that drive engagement."
        },
        {
          title: "Sales-focused Messaging",
          desc: "Copywriting that speaks directly to your dream customer's pain."
        },
        {
          title: "Lead Capture System",
          desc: "The infrastructure to turn views into actual contact info."
        },
        {
          title: "Simple Follow-up Automation",
          desc: "Automated sequences so you never have to manual email again."
        }
      ]
    },
    process: {
      h2: "We build customer acquisition systems",
      steps: [
        {
          step: "STEP 1",
          title: "Clarify your offer",
          desc: "We strip away the noise and find exactly what people want to buy from you."
        },
        {
          step: "STEP 2",
          title: "Create content people trust",
          desc: "We produce high-quality assets that prove you are the expert."
        },
        {
          step: "STEP 3",
          title: "Capture leads",
          desc: "We install the plumbing to catch the attention we've generated."
        },
        {
          step: "STEP 4",
          title: "Follow up automatically",
          desc: "Systems take over to nurture leads while you focus on your work."
        },
        {
          step: "STEP 5",
          title: "Generate repeat customers",
          desc: "We turn one-time buyers into loyal brand advocates."
        }
      ]
    },
    whyMe: {
      tag: "THE DIFFERENCE",
      h2: "Why work with me?",
      quote: "Before I ever picked up a camera, I spent ",
      quoteSpan1: "10 years selling to businesses.",
      quote2: " That's why I don't start with content. I start with ",
      quoteSpan2: "understanding why customers buy."
    },
    pillars: {
      items: [
        {
          title: "Sales-first messaging",
          desc: "People don't buy services. They buy outcomes. I help you communicate your value clearly."
        },
        {
          title: "Visuals that build trust",
          desc: "Professional video and photography that instantly increases trust before you even speak to a customer."
        },
        {
          title: "Systems",
          desc: "Simple funnels, email sequences and customer journeys that keep working after the content is published."
        },
        {
          title: "AI & Automation",
          desc: "Spend less time on repetitive work and more time running your business."
        }
      ]
    },
    finalCta: {
      h2: "Fix Your Sales Funnel",
      p: "Ready to stop guessing and start closing? Book a free 15-minute strategy call with me below. We will look at your current website or offer and find at least one quick fix you can implement this week to get more clients.",
      cta: "Free 15-minute Growth Review",
      subtext: "You'll leave the call with at least one actionable idea—even if we never work together."
    },
    footer: {
      desc: "Helping local businesses turn their expertise into high-converting systems.",
      imprint: "Imprint",
      privacy: "Privacy Policy"
    },
    privacy: {
      title: "Privacy Policy",
      effective: "Effective Date: 27.07.2026",
      intro: "We respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website, download our free resources, book an appointment, or subscribe to our newsletter.",
      sections: [
        {
          h2: "1. Information We Collect",
          content: "We believe in data minmization and only collect the information absolutely necessary to provide you with the content and services you request.\n\n• For PDF Freebie Downloads & Newsletter Subscribers: When you sign up to receive our free PDF or opt-in to our newsletter, we collect your email address.\n• For Booking Appointments: When you schedule a meeting or consultation with us via TidyCal, we collect your name, email address, and any other details you provide during the booking process."
        },
        {
          h2: "2. How We Use Your Data",
          content: "We use the information we collect strictly for the following purposes:\n\n• To deliver the requested PDF freebie to your inbox.\n• To schedule, manage, and confirm your appointments.\n• To send you marketing newsletters and fitness updates (only if you have explicitly opted in)."
        },
        {
          h2: "3. Cookies",
          content: "We respect your privacy and do not use invasive tracking, advertising, or third-party analytics cookies on your device. Any cookies used by our website hosting platform are strictly essential for the basic technical functioning and security of the site."
        },
        {
          h2: "4. Third-Party Service Providers & External Links",
          content: "We do not sell, rent, or share your personal data with marketing firms or data brokers. We only share necessary data with trusted third-party platforms that help us run our site:\n\n• SendFox (Newsletter and Email Delivery): We require your email address to send you our PDF freebie and newsletters. We use SendFox, a service provided by Sumo Group Inc., 1305 E 6th St #3, Austin, TX 78702, USA.\n• TidyCal (Appointment Bookings): We use TidyCal, a service provided by AppSumo Inc., 1305 E 6th St #3, Austin, TX 78702, USA, to manage our bookings.\n• Google Drive (File Hosting): We host our downloadable PDF freebie on Google Drive. Google may collect standard web traffic data (like your IP address) when you access their servers.\n• Social Media Links (Instagram): Our website contains links to our Instagram profile operated by Meta Platforms Ireland Limited."
        },
        {
          h2: "5. Your Rights and Choices",
          content: "You have full control over your personal data. At any time, you have the right to:\n\n• Access, correct, or delete your personal information.\n• Opt-out of marketing emails by clicking the 'unsubscribe' link.\n• Request complete erasure via contact@mdlens.com."
        },
        {
          h2: "6. Contact Us",
          content: "If you have any questions or concerns about this Privacy Policy or how we handle your data, please reach out to us at contact@mdlens.com."
        }
      ]
    },
    imprint: {
      title: "Imprint (Legal Notice)",
      back: "Back to Home",
      sections: [
        { label: "Responsible for Content", value: "Mathias Deppe" },
        { label: "Address", value: "Nemesi Valls 42\n08210 Barberà del Vallès\nBarcelona, Spain" },
        { label: "Contact & Support", value: "Email: contact@mdlens.com\nPhone: +34 623 780 182" },
        { label: "Tax Identification", value: "VAT/Tax ID (NIF/NIE): Y8187448Z" }
      ],
      dispute: {
        title: "EU Dispute Resolution",
        content: "The European Commission provides a platform for online dispute resolution (ODR): https://ec.europa.eu/consumers/odr. We are neither willing nor obliged to participate in dispute resolution proceedings before a consumer arbitration board."
      }
    }
  },
  es: {
    nav: {
      cta: "Reserva tu Auditoría"
    },
    hub: {
      liveProof: "Descubre lo que puede hacer el Asistente de IA →",
      contentDay: "Explorar el Día de Contenido →",
      aiAutomation: "Ver el Asistente de IA →"
    },
    hero: {
      h1: "No necesitas más contenido.",
      h1Span: "Necesitas un sistema que atraiga clientes constantemente.",
      h2: "Las grandes empresas fracasan todos los días porque nadie sabe que existen. Yo soluciono eso. Construyo sistemas de adquisición de clientes que convierten a desconocidos en clientes de pago.",
      p: "Ayudo a negocios locales a transformar su experiencia en mensajes claros, contenido premium y sistemas de ventas sencillos que generan más leads.",
      cta: "Reserva una auditoría de crecimiento"
    },
    liveDemo: {
      title: "Pruébalo en Vivo: Automatización de DMs",
      body: "No te quedes solo con mis palabras. Prueba nuestro sistema de adquisición por DM en tiempo real. Haz clic abajo, envíame un DM con la palabra 'SISTEMA' en Instagram y mira cómo el bot te cualifica al instante.",
      cta: "Pruébalo en Instagram"
    },
    beautyClinic: {
      hero: {
        h1: "Convierte las consultas de Instagram en más citas de estética",
        h2: "Tu clínica ya recibe DMs. El problema es qué pasa cuando alguien pregunta: '¿Cuánto cuesta el Botox?', '¿Ofrecen este tratamiento?', '¿Puedo reservar esta semana?'",
        body: "Tu Asistente de Leads con IA responde al instante, resuelve dudas frecuentes, cualifica las consultas y guía a los prospectos adecuados hacia una consulta o cita.",
        pricing: "99 €/mes + 10 % de los ingresos atribuidos del primer tratamiento | Sin nuevos comerciales. Sin CRMs complejos. Sin necesidad de inversión publicitaria.",
        cta: "Reserva una Auditoría de Crecimiento Gratuita →"
      },
      problem: {
        h2: "Tus DMs de Instagram están llenos de oportunidades.",
        subtext: "Pero cada pregunta sin respuesta, respuesta lenta o seguimiento olvidado puede significar una cita perdida.",
        items: [
          {
            title: "Respuestas lentas",
            desc: "Los clientes potenciales se van si no reciben una respuesta en cuestión de minutos."
          },
          {
            title: "Preguntas repetitivas",
            desc: "Tu equipo responde una y otra vez las mismas dudas sobre tratamientos y precios."
          },
          {
            title: "Consultas no cualificadas",
            desc: "Tu equipo pierde tiempo en conversaciones que nunca se convierten en citas reales."
          },
          {
            title: "Seguimientos perdidos",
            desc: "Las conversaciones mueren porque no hay un sistema para mantenerlas activas."
          }
        ],
        closing: "No necesariamente necesitas más consultas. Necesitas gestionar mejor las que ya tienes."
      },
      howItWorks: {
        h2: "Tu Asistente de Leads con IA trabaja dentro de la conversación.",
        items: [
          {
            title: "Respuesta instantánea",
            desc: "Las nuevas consultas reciben respuesta inmediata sin esperar a tu ocupado equipo."
          },
          {
            title: "Entiende la petición",
            desc: "El agente lee el contexto y se adapta a lo que la persona realmente desea."
          },
          {
            title: "Resuelve dudas comunes",
            desc: "Info de tratamientos, disponibilidad y precios — basada en la información que tú proporciones."
          },
          {
            title: "Cualificación",
            desc: "Identifica si la persona es un prospecto serio y filtra el ruido innecesario."
          },
          {
            title: "Impulsa la reserva",
            desc: "Guía a los prospectos cualificados hacia un enlace de consulta o cita."
          },
          {
            title: "Seguimiento",
            desc: "Reactiva conversaciones que se enfrían cuando dejan de responder."
          }
        ]
      },
      journey: {
        h2: "Del DM de Instagram a la Cita",
        steps: ["DM Instagram", "Respuesta Instantánea", "Dudas (Q&A)", "Cualificación", "Consulta", "Cita"],
        subtext: "Tu equipo dedica menos tiempo a responder preguntas repetitivas y más tiempo a los clientes reales."
      },
      trust: {
        h2: "Diseñado para conversaciones reales de clínicas",
        medical: "IMPORTANTE: El Asistente de IA no realiza diagnósticos, no da asesoramiento médico individual ni recomienda tratamientos no aprobados. Solo utiliza información explícitamente aprobada por su clínica.",
        cards: [
          {
            title: "Dudas sobre Tratamientos",
            desc: "'¿Es seguro este láser para pieles oscuras?'"
          },
          {
            title: "Dudas sobre Precios",
            desc: "'¿Cuánto cuesta una sesión vs un pack?'"
          },
          {
            title: "Disponibilidad",
            desc: "'¿Tenéis algún hueco este viernes?'"
          },
          {
            title: "Cualificación",
            desc: "'¿Se ha realizado este tratamiento antes?'"
          },
          {
            title: "Reserva",
            desc: "'¿Puedo reservar mi consulta ahora?'"
          },
          {
            title: "Seguimiento",
            desc: "'¿Sigues interesada en esa sesión?'"
          }
        ]
      },
      positioning: {
        h2: "Esto no es un chatbot.",
        text: "La mayoría de los chatbots están diseñados para responder preguntas. Este sistema está diseñado para hacer avanzar la conversación.",
        logic: ["Pregunta", "Contexto", "Cualificación", "Siguiente paso"]
      },
      whyClinics: {
        h2: "Por qué funciona para negocios de estética",
        items: ["Preguntas repetitivas sobre detalles de tratamientos.", "Consultas urgentes (la velocidad es clave).", "Fácil de cualificar bajo criterios sencillos.", "Conectado directamente a una reserva de alto valor."],
        conclusion: "Tu equipo no debería gestionar cada primera interacción. La IA se encarga de la primera capa. Tu equipo se encarga del cliente."
      },
      fit: {
        do: {
          title: "Cuándo funciona mejor",
          items: ["Consultas regulares en Instagram", "Oferta de tratamientos clara", "Proceso de reserva definido", "FAQs e info aprobada"]
        },
        dont: {
          title: "No es para ti si",
          items: ["Rara vez recibes consultas", "Tu oferta aún no es clara", "Esperas que la IA cree demanda de la nada"]
        }
      },
      pricing: {
        h2: "Asistente de Leads con IA",
        price: "99 € / mes + 10 % ingresos primer tratamiento",
        included: ["Asistente de IA para DMs de Instagram", "Lógica de conversación personalizada", "Cualificación de leads", "Gestión de preguntas frecuentes", "Guía para la reserva", "Automatización de seguimiento", "Optimización continua"]
      },
      implementation: {
        steps: [
          {
            title: "1. Mapeo de Flujo",
            desc: "Definimos qué pregunta la gente y cuándo reservar."
          },
          {
            title: "2. Construcción",
            desc: "Configurado con la info aprobada de tu clínica."
          },
          {
            title: "3. Pruebas",
            desc: "Simulamos escenarios reales antes del lanzamiento."
          },
          {
            title: "4. Lanzamiento",
            desc: "El sistema empieza a gestionar la primera capa."
          },
          {
            title: "5. Optimización",
            desc: "Mejoramos las respuestas basadas en datos reales."
          }
        ]
      },
      proof: {
        h2: "No te fíes de mi palabra. Habla tú mismo con la IA.",
        body: "Uso el mismo sistema en mi Instagram que ofrezco a negocios locales. Envía 'SISTEMA' en Instagram y vive la experiencia tú mismo.",
        cta: "Probar la IA →"
      },
      finalCta: {
        h2: "¿Podría beneficiarse tu clínica de esto?",
        body: "Reserva una Auditoría de Crecimiento gratuita de 15 minutos. Analizaremos cómo se gestionan tus consultas actuales y veremos si un asistente de IA podría ahorrar tiempo a tu equipo.",
        cta: "Reserva mi Auditoría →"
      }
    },
    aiLeadAgent: {
      hero: {
        h1: "Conoce a tu Asistente de Leads con IA",
        h2: "Tus DMs de Instagram no deberían ser solo chats. Deberían crear clientes.",
        subH2: "Diseñado para convertir conversaciones de Instagram en oportunidades cualificadas.",
        body: "El asistente responde a consultas, resuelve dudas, cualifica prospectos y guía a las personas adecuadas hacia una reserva — de forma totalmente automática.",
        cta: "Ver en Acción →",
        proof: "Ya estás hablando con él. Pruébalo tú mismo. → Envía 'SISTEMA' en Instagram."
      },
      whatItDoes: {
        h2: "¿Qué pasa cuando alguien escribe a tu negocio?",
        items: [
          {
            title: "Respuesta instantánea",
            desc: "Cuanto más rápido respondas, menos probable será que una consulta prometedora se enfríe."
          },
          {
            title: "Entiende al prospecto",
            desc: "El agente lee la conversación y adapta sus preguntas a las necesidades específicas de la persona."
          },
          {
            title: "Responde dudas frecuentes",
            desc: "Responde preguntas comunes sobre tus servicios, disponibilidad, precios y próximos pasos — basándose en la información que tú le proporciones."
          },
          {
            title: "Cualificación",
            desc: "Identifica prospectos serios y prioriza las conversaciones que merecen la atención de tu equipo."
          },
          {
            title: "Impulsa la conversión",
            desc: "Guía a los leads cualificados hacia una reserva y reduce los interminables mensajes para cuadrar horarios."
          },
          {
            title: "Seguimiento",
            desc: "Reactiva automáticamente las conversaciones que se enfrían, asegurando que ningún lead se pierda."
          }
        ]
      },
      flow: {
        h2: "El Viaje Sin Fricciones",
        steps: ["DM Instagram", "Respuesta", "Conversación", "Cualificación", "Reserva"],
        teamHandover: "Tu equipo toma el relevo a partir de ahí.",
        subtext: "Sin formularios. Sin embudos complejos. Solo una mejor conversación."
      },
      capabilities: {
        h2: "Capacidades del Sistema",
        items: [
          {
            title: "Conversaciones humanas",
            desc: "La IA entiende el contexto y adapta sus respuestas en lugar de seguir un guion rígido y robótico."
          },
          {
            title: "Entiende más que texto",
            desc: "Los prospectos pueden enviar audios o imágenes, y el agente usa esa info para responder mejor."
          },
          {
            title: "Reserva de citas",
            desc: "Guía a los leads cualificados hacia una reserva y reduce los interminables mensajes para cuadrar horarios."
          },
          {
            title: "Seguimiento automático",
            desc: "Si un prospecto deja de responder, el agente hace un seguimiento suave para devolverlo al paso de reserva."
          },
          {
            title: "Nativo en Instagram",
            desc: "Opera directamente donde tu audiencia ya pasa su tiempo, eliminando la fricción de plataforma."
          }
        ]
      },
      difference: {
        text: "Esto no es un chatbot. La mayoría de los chatbots solo responden preguntas. Este sistema está diseñado para hacer avanzar la conversación.",
        logic: ["Pregunta", "Contexto", "Cualificación", "Siguiente paso"]
      },
      whoIsItFor: {
        h2: "Diseñado para negocios que ya reciben consultas",
        disclaimer: "CRÍTICO: Si aún no recibes consultas entrantes constantes, un agente de IA por DM probablemente no sea tu prioridad actual.",
        items: [
          {
            title: "Gimnasios",
            desc: "Cualifica consultas, responde preguntas comunes y ayuda a convertir conversaciones de Instagram en pruebas reservadas."
          },
          {
            title: "Coaches",
            desc: "Filtra prospectos + reserva llamadas de descubrimiento en piloto automático."
          },
          {
            title: "Servicios Locales",
            desc: "Convierte consultas iniciales en presupuestos firmes o reservas."
          },
          {
            title: "Clínicas",
            desc: "Gestiona FAQs + cualifica pacientes + reserva citas médicas."
          },
          {
            title: "Hostelería",
            desc: "Gestiona disponibilidad, reservas y preguntas comunes de los clientes."
          }
        ]
      },
      comparison: {
        sense: {
          title: "Cuándo tiene sentido un Asistente de Leads con IA",
          items: ["Ya recibes consultas entrantes.", "Tu equipo dedica mucho tiempo a responder preguntas repetitivas.", "Algunos prospectos desaparecen antes de llegar a reservar.", "Quieres responder más rápido sin tener que contratar a otra persona."]
        },
        noSense: {
          title: "Cuándo no tiene sentido",
          items: ["Rara vez recibes consultas de forma orgánica.", "Tu oferta aún no está del todo clara.", "Todavía estás definiendo quién es tu cliente ideal."]
        }
      },
      proof: {
        h2: "No leas sobre ello. Habla con él.",
        subH2: "Estás viendo una página sobre un asistente de ventas de IA. Así que en lugar de explicarte cómo funciona, pruébalo tú mismo.",
        steps: ["Abre Instagram", "Envía 'SISTEMA'", "Ten una conversación real", "Mira si es capaz de cualificarte"],
        cta: "Habla con la IA →"
      },
      finalCta: {
        h2: "¿Podría funcionar esto en tu negocio?",
        body: "Reserva una Revisión de Crecimiento gratuita de 15 minutos. Analizaremos tu flujo actual de consultas e identificaremos dónde la automatización podría ahorrar tiempo, mejorar el seguimiento o ayudar a que más prospectos den el siguiente paso.",
        cta: "Reservar Auditoría →"
      }
    },
    guarantee: {
      title: "La Garantía",
      body: "Si no generas 5 leads cualificados en 14 días, sigo trabajando sin coste adicional hasta que lo hagamos. La garantía se aplica cuando la oferta acordada, la base de datos de leads, el sistema de reservas y el proceso de respuesta están implementados y se utilizan activamente."
    },
    fitness: {
      seo: {
        title: "Desbloquea 500 € – 1.500 € en 14 Días | Mathias Deppe",
        description: "ATENCIÓN PROPIETARIOS DE GIMNASIOS EN BARCELONA: Desbloquea entre 500 € y 1.500 € en 14 días de tu lista de contactos 'muertos' — Con 0 € en publicidad."
      },
      hero: {
        tag: "ATENCIÓN PROPIETARIOS DE GIMNASIOS EN BARCELONA",
        h1: "Desbloquea entre 500 € y 1.500 € en 14 días de tu lista de contactos 'muertos'",
        h1Sub: "— Con 0 € en publicidad.",
        hook: "No necesitas más inicios de sesión en software, retenciones de agencias genéricas o horas bailando en TikTok.",
        p: "No necesitas otro inicio de sesión en un software complejo ni una cuota mensual de agencia genérica. Necesitas liquidez en tu cuenta bancaria. Me presento en tu gimnasio en Barcelona, grabo contenido con calidad de cine y convierto tus antiguos leads sin cerrar en miembros de alto valor en menos de 48 horas.",
        pricing: "249 €/mes + 30 € por nuevo miembro | Sin nuevos comerciales.",
        cta: "Reserva tu Auditoría Gratuita →"
      },
      vip: {
        header: "El Paquete VIP Kickoff 'Barcelona Local' (Valor Real de 4.500 €)",
        intro: "No solo te envío videos de 'cómo hacerlo'. Me presento en tus instalaciones con equipo profesional para construir los activos de tu negocio por ti.",
        items: [
          {
            title: "Sesión de Contenido Pro de 3 Horas In Situ (Valor de 1.500 €)",
            desc: "Llevo equipo de cine a tu gimnasio en Barcelona. Capturamos más de 20 fotos pro y 8 reels de alta conversión para que seas la autoridad nº1 de la ciudad."
          },
          {
            title: "Configuración Completa de Sistemas (Valor de 1.000 €)",
            desc: "Páginas de aterrizaje high-ticket personalizadas, calendarios de reserva automáticos e infraestructura de respuesta por SMS en 5 min."
          },
          {
            title: "Campaña de Inyección de Efectivo (Valor de 1.000 €)",
            desc: "Secuencia dedicada de reactivación de base de datos para tus antiguos leads. Convertimos contactos 'muertos' en dinero con 0 € en anuncios."
          },
          {
            title: "Motor de Reservas DM en Autopiloto 24/7 (Valor Real de 1.000 €)",
            desc: "Construimos, guionizamos y lanzamos flujos de conversación automatizados en Instagram y WhatsApp dentro de tus cuentas. No vuelvas a perder un lead ni a perder horas respondiendo preguntas básicas por DM."
          }
        ]
      },
      comparison: {
        h2: "La Trampa de las Agencias y Consultores",
        p: "La mayoría de las agencias te dan un CRM y te dicen que te grabes tú mismo. Los consultores te dan consejos pero no ejecución. Yo soy el puente.",
        headers: ["La Trampa de la Agencia", "La Trampa del Consultor", "El Sistema MD"],
        agency: {
          title: "La Trampa de la Agencia",
          points: ["Te dan un CRM complejo", "Te exigen que grabes con el móvil", "Te fuerzan a gastar +2k € en anuncios", "Te atan con contratos de 6 meses"]
        },
        consultant: {
          title: "The Consultant Trap",
          points: ["Te dan PDFs de 'estrategia'", "Te dejan todo el trabajo a ti", "Sin implementación técnica", "Cobran por horas"]
        },
        md: {
          title: "El Sistema MD",
          points: ["Yo monto todas las webs y automatizaciones", "Vengo con equipo pro para grabar", "0 € en anuncios para la Fase 1", "Garantía estricta de resultados en 14 días"]
        }
      },
      howItWorks: {
        h2: "El Sistema de Crecimiento en 4 Fases",
        phases: [
          {
            title: "Fase 1: Inyección de Efectivo",
            desc: "Enviamos un mensaje de 9 palabras a tu lista de contactos inactivos. Convertimos antiguos leads en dinero inmediato en 48 horas."
          },
          {
            title: "Phase 2: Oferta Grand Slam",
            desc: "Sustituimos tu membresía habitual de 120 €/mes por un Desafío de Transformación de 6 semanas por 500 €. Dinero por adelantado."
          },
          {
            title: "Fase 3: Motor Inbound 'Speed-to-Lead' de 30 Segundos",
            desc: "Instalamos nuestra automatización de mensajes directos pre-guionizada para Instagram, Facebook y WhatsApp en tus cuentas. Cuando un cliente potencial comenta un Reel, hace clic en un anuncio o envía un mensaje, nuestro sistema responde al instante en 30 segundos, lo pre-cualifica y le envía el enlace de reserva de tu Desafío de 6 Semanas mientras duermes."
          },
          {
            title: "Fase 4: Renovación a 12 Meses",
            desc: "Convertimos a los participantes del desafío en contratos a largo plazo. Te quedas el dinero inicial y aseguras ingresos recurrentes."
          }
        ]
      },
      investment: {
        h2: "Precios Basados en Rendimiento",
        intro: "No me escondo tras el 'branding'. Mis precios se basan en los sistemas que construyo y los resultados que generan.",
        setup: {
          title: "La Construcción",
          price: "2.000 € Cuota Inicial",
          desc: "Montaje de embudos, copy, automatizaciones y la campaña de inyección de efectivo de 14 días."
        },
        tier1: {
          title: "El Mínimo",
          price: "500 €/mes",
          desc: "Gestión de anuncios y mantenimiento. Tú grabas clips con el móvil; yo me encargo de toda la parte técnica."
        },
        tier2: {
          title: "El Premium",
          price: "1.500 €/mes",
          desc: "Gestión completa MÁS una sesión de contenido pro de 3 horas en tu gimnasio en Barcelona cada mes."
        },
        waived: {
          title: "La Oferta de Socio",
          price: "0 €",
          desc: "Comprométete a 12 meses en cualquier nivel y elimino totalmente la cuota de configuración de 2.000 €."
        }
      },
      faq: {
        h2: "Verdades Incómodas (FAQ)",
        items: [
          {
            q: "¿Por qué no me das el software y lo hago yo?",
            a: "Porque el software no construye negocios, los sistemas sí. Eres dueño de un gimnasio, no ingeniero de sistemas. Yo monto la estructura para que tú te enfoques en entrenar."
          },
          {
            q: "Ya me han engañado agencias antes. ¿Por qué esto es distinto?",
            a: "Agencies focus on 'leads.' I focus on 'cash.' We start with your existing list (Phase 1) to get you a ROI before you even spend money on ads. If I don't perform, I work for free."
          },
          {
            q: "¿Tengo que salir en cámara?",
            a: "Sí, pero lo hago fácil. Vengo a tu gimnasio, pongo las luces y te dirijo. No necesitas ser actor, solo el experto que ya eres."
          }
        ]
      },
      guarantee: {
        title: "La Garantía de Leads en Barcelona",
        desc: "Si no generas 5 leads cualificados en 14 días, sigo trabajando sin coste adicional hasta que lo hagamos. La garantía se aplica cuando la oferta acordada, la base de datos de leads, el sistema de reservas y el proceso de respuesta están implementados y se utilizan activamente.",
        cta: "Solicita tu Inyección de Efectivo de 14 Días"
      }
    },
    problem: {
      h2: "Sabes que tu negocio es excelente.",
      h2Span: "¿Entonces por qué no compran más personas?",
      points: [
        {
          title: "Mensajes Confusos",
          desc: "Tu audiencia no entiende exactamente qué haces o por qué es importante."
        },
        {
          title: "Contenido Inconsistente",
          desc: "Publicas cuando tienes tiempo, lo que significa que nunca generas inercia."
        },
        {
          title: "Sin Viaje del Cliente",
          desc: "No hay un camino claro desde que ven un post hasta que se convierten en clientes."
        },
        {
          title: "Sin Seguimiento",
          desc: "Los prospectos se pierden porque no tienes un sistema para nutrirlos."
        }
      ],
      result: "El resultado? Le gustas a la gente…",
      resultSpan: "pero nunca se convierten en clientes.",
      cta: "Mira cómo resolvimos esto para otros →",
      viewAll: "Ver todos los casos de éxito →"
    },
    caseStudies: {
      header: "Casos de Éxito / Resultados",
      viewFull: "Ver Caso de Éxito Completo",
      back: "Volver al inicio",
      challenge: "El Desafío",
      solution: "La Solución",
      results: "Los Resultados"
    },
    deliverables: {
      h2: "Así es como trabajamos juntos",
      items: [
        {
          title: "1 Día de Contenido al Mes",
          desc: "Un día completo dedicado a capturar todos los recursos que necesitas."
        },
        {
          title: "20+ Fotos Profesionales",
          desc: "Fotografía de alto nivel que te posiciona como una autoridad."
        },
        {
          title: "6–8 Videos Cortos",
          desc: "Reels y TikToks listos para viralizar que impulsan el engagement."
        },
        {
          title: "Mensajes Enfocados en Ventas",
          desc: "Copywriting que habla directamente a los problemas de tu cliente ideal."
        },
        {
          title: "Sistema de Captación de Leads",
          desc: "La infraestructura para convertir visualizaciones en contactos reales."
        },
        {
          title: "Automatización de Seguimiento",
          desc: "Secuencias automáticas para que no tengas que enviar correos manuales."
        }
      ]
    },
    process: {
      h2: "Construimos sistemas de adquisición de clientes",
      steps: [
        {
          step: "PASO 1",
          title: "Clarifica tu oferta",
          desc: "Eliminamos el ruido y encontramos exactamente qué es lo que la gente quiere comprarte."
        },
        {
          step: "PASO 2",
          title: "Crea contenido de confianza",
          desc: "Producimos activos de alta calidad que demuestran que eres el experto."
        },
        {
          step: "PASO 3",
          title: "Capta prospectos",
          desc: "Instalamos el sistema para retener la atención que hemos generado."
        },
        {
          step: "PASO 4",
          title: "Seguimiento automático",
          desc: "Los sistemas se encargan de nutrir a los leads mientras tú te enfocas en tu trabajo."
        },
        {
          step: "PASO 5",
          title: "Genera clientes recurrentes",
          desc: "Convertimos a los compradores de una vez en defensores leales de tu marca."
        }
      ]
    },
    whyMe: {
      tag: "LA DIFERENCIA",
      h2: "¿Por qué trabajar conmigo?",
      quote: "Antes de coger una cámara, pasé ",
      quoteSpan1: "10 años vendiendo a empresas.",
      quote2: " Por eso no empiezo con el contenido. Empiezo ",
      quoteSpan2: "entendiendo por qué compran los clientes."
    },
    pillars: {
      items: [
        {
          title: "Mensajes enfocados en ventas",
          desc: "La gente no compra servicios. Compra resultados. Te ayudo a comunicar tu valor con claridad."
        },
        {
          title: "Visuales que generan confianza",
          desc: "Video y fotografía profesional que aumenta la confianza antes siquiera de hablar con el cliente."
        },
        {
          title: "Sistemas",
          desc: "Embudos simples, secuencias de email y viajes del cliente que siguen funcionando tras publicar el contenido."
        },
        {
          title: "IA y Automatización",
          desc: "Dedica menos tiempo a tareas repetitivas y más tiempo a dirigir tu negocio."
        }
      ]
    },
    finalCta: {
      h2: "Arregla tu Embudo de Ventas",
      p: "¿Listo para dejar de adivinar y empezar a cerrar ventas? Reserva una llamada estratégica gratuita de 15 minutos. Analizaremos tu web u oferta actual y encontraremos al menos una solución rápida que puedas aplicar esta misma semana.",
      cta: "Auditoría de Crecimiento Gratuita",
      subtext: "Saldrás de la llamada con al menos una idea accionable, incluso si nunca trabajamos juntos."
    },
    footer: {
      desc: "Ayudando a negocios locales a convertir su experiencia en sistemas de alta conversión.",
      imprint: "Aviso Legal",
      privacy: "Privacidad"
    },
    privacy: {
      title: "Política de Privacidad",
      effective: "Fecha de vigencia: 27.07.2026",
      intro: "Respetamos su privacidad y estamos comprometidos a proteger sus datos personales. Esta Política de Privacidad explica cómo recopilamos, utilizamos y protegemos su información cuando visita nuestro sitio web, descarga nuestros recursos gratuitos, reserva una cita o se suscribe a nuestro boletín (newsletter).",
      sections: [
        {
          h2: "1. Información que recopilamos",
          content: "Creemos en la minimización de datos y solo recopilamos la información absolutamente necesaria para proporcionarle el contenido y los servicios que solicita.\n\n• Para descargas del PDF gratuito y suscriptores del boletín: Recopilamos su dirección de correo electrónico.\n• Para la reserva de citas: Recopilamos su nombre, dirección de correo electrónico y cualquier otro detalle que proporcione durante el proceso de reserva en TidyCal."
        },
        {
          h2: "2. Cómo utilizamos sus datos",
          content: "Utilizamos la información estrictamente para:\n\n• Enviar el PDF gratuito solicitado.\n• Programar, gestionar y confirmar sus citas.\n• Enviar boletines de marketing (solo con su consentimiento explícito)."
        },
        {
          h2: "3. Cookies",
          content: "Respetamos su privacidad y no utilizamos cookies invasivas de rastreo o publicidad. Solo utilizamos cookies técnicas esenciales para el funcionamiento básico del sitio."
        },
        {
          h2: "4. Proveedores de servicios de terceros y Enlaces Externos",
          content: "Solo compartimos datos con plataformas de confianza:\n\n• SendFox: Para el envío de boletines y recursos gratuitos.\n• TidyCal: Para gestionar nuestras reservas de citas.\n• Google Drive: Donde alojamos nuestro PDF gratuito descargable.\n• Instagram: Enlace a nuestro perfil operado por Meta Platforms."
        },
        {
          h2: "5. Sus derechos y opciones",
          content: "Usted tiene derecho a:\n\n• Acceder, corregir o eliminar su información.\n• Darse de baja de los correos haciendo clic en 'cancelar suscripción'.\n• Solicitar la eliminación completa a través de contact@mdlens.com."
        },
        {
          h2: "6. Contactos",
          content: "Si tiene alguna pregunta sobre esta Política de Privacidad, comuníquese con nosotros en contact@mdlens.com."
        }
      ]
    },
    imprint: {
      title: "Aviso Legal",
      back: "Volver al inicio",
      sections: [
        { label: "Responsable del contenido", value: "Mathias Deppe" },
        { label: "Dirección", value: "Nemesi Valls 42\n08210 Barberà del Vallès\nBarcelona, España" },
        { label: "Contacto y Soporte", value: "Correo electrónico: contact@mdlens.com\n Teléfono: +34 623 780 182" },
        { label: "Identificación Fiscal", value: "NIF/NIE: Y8187448Z" }
      ],
      dispute: {
        title: "Resolución de Litigios de la UE",
        content: "La Comisión Europea ofrece una plataforma para la resolución de litigios en línea (RLL): https://ec.europa.eu/consumers/odr. No estamos dispuestos ni obligados a participar en procedimientos de resolución de conflictos ante una junta de arbitraje de consumo."
      }
    }
  }
};